import { apiService } from '@shared/services/apiServices';
import certificateEndpoints from './certificateEndpoints';
import type {
  ListCertificateEquipmentData,
  ListCertificateEquipmentParams,
} from '../types/myCertificateTypes';

const buildEmpty = (
  page: number,
  limit: number
): ListCertificateEquipmentData => ({
  items: [],
  total: 0,
  page,
  limit,
});

/**
 * Fetches a paginated list of distinct equipment accessible to the
 * authenticated caller, used to populate the equipment filter dropdown
 * on the certificate list tabs.
 *
 * CLIENT sees equipment under their own facilities (requires equipment_list AVP).
 * ADMIN/SUPER_ADMIN see all equipment.
 * TECHNICIAN receives 403.
 *
 * Supports server-side search by equipment name or equipment_id via `search`,
 * and infinite-scroll pagination via `page`/`limit`.
 *
 * Pass `lookupIds` (equipment UUIDs) to bypass search and pagination and return
 * only those specific items — used to resolve display labels for filter IDs
 * restored from URL params that may not be in the first loaded page.
 *
 * @returns Paginated data envelope, or an empty envelope on error.
 */
export async function listCertificateEquipment(
  params: ListCertificateEquipmentParams = {}
): Promise<ListCertificateEquipmentData> {
  const qs = new URLSearchParams();
  if (params.page) {
    qs.append('page', String(params.page));
  }
  if (params.limit) {
    qs.append('limit', String(params.limit));
  }
  if (params.search) {
    qs.append('search', params.search);
  }
  if (params.lookupIds && params.lookupIds.length > 0) {
    qs.append('lookupIds', params.lookupIds.join(','));
  }

  const fallbackPage = params.page ?? 1;
  const fallbackLimit = params.limit ?? 50;
  const endpoint = `${certificateEndpoints.EQUIPMENT}${qs.toString() ? `?${qs.toString()}` : ''}`;

  const response = await apiService.getAPI(endpoint, {
    skipGlobalErrorRedirect: true,
  });
  if (response.error) {
    return buildEmpty(fallbackPage, fallbackLimit);
  }

  const payload = (response.data ?? {}) as Record<string, unknown>;
  const rawData = payload['data'];

  // Handle both flat-array and paginated ({ items, total, page, limit }) shapes.
  if (Array.isArray(rawData)) {
    const items = rawData as ListCertificateEquipmentData['items'];
    return {
      items,
      total: items.length,
      page: fallbackPage,
      limit: items.length || fallbackLimit,
    };
  }

  const inner = (rawData ?? {}) as Record<string, unknown>;
  const items = Array.isArray(inner['items'])
    ? (inner['items'] as ListCertificateEquipmentData['items'])
    : [];

  return {
    items,
    total: Number(inner['total'] ?? 0),
    page: Number(inner['page'] ?? fallbackPage),
    limit: Number(inner['limit'] ?? fallbackLimit),
  };
}
